import React, { useState, useContext } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
    Button,
    CssBaseline,
    TextField,
    Link,
    Grid,
    Box,
    Typography,
    IconButton,
    InputAdornment,
    Paper,
} from "@mui/material";
import { makeStyles } from 'tss-react/mui';
import { Visibility, VisibilityOff } from "@mui/icons-material";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import logo from "../../assets/logologin.png";
import wallpaper from "../../assets/wallpaper.webp";
import wallpaperVideo from "../../assets/wallpaper.mp4";

const useStyles = makeStyles()((theme) => ({
	root: {
		minHeight: "100vh",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		position: "relative",
		overflow: "hidden",
		backgroundColor: "#101216",
	},
	video: {
		position: "absolute",
		top: 0,
		left: 0,
		width: "100%",
		height: "100%",
		objectFit: "cover",
	},
	overlay: {
		position: "absolute",
		inset: 0,
		backgroundColor: "rgba(16, 18, 22, 0.72)",
	},
	card: {
		position: "relative",
		padding: theme.spacing(5, 4, 3),
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		width: 400,
		backgroundColor: theme.palette.background.paper,
		boxShadow: "0 20px 50px rgba(0,0,0,0.45)",
		borderRadius: 16,
	},
	logo: {
		width: 150,
		marginBottom: theme.spacing(1),
	},
	form: {
		width: "100%",
		marginTop: theme.spacing(1),
	},
	submitButton: {
		margin: theme.spacing(3, 0, 1),
		height: 42,
		fontSize: 14,
	},
	registerButton: {
		margin: theme.spacing(0.5, 0, 1),
		height: 42,
		fontSize: 14,
	},
	link: {
		marginTop: theme.spacing(1.5),
		fontSize: 13,
	},
	copyright: {
		marginTop: theme.spacing(3),
	},
}));

const Copyright = () => (
	<Typography
		variant="body2"
		color="textSecondary"
		align="center"
		style={{ fontSize: 12 }}
	>
		{"© "}
		{new Date().getFullYear()}{" "}
		<Link
			color="inherit"
			underline="hover"
			href="https://www.linkedin.com/in/luigy-miranda"
			target="_blank"
			rel="noopener noreferrer"
		>
			ChatEcarga
		</Link>
		{" · Todos los derechos reservados"}
	</Typography>
);

const Login = () => {
	const { classes } = useStyles();
	const [user, setUser] = useState({ email: "", password: "" });
	const [showPassword, setShowPassword] = useState(false);
	const { handleLogin } = useContext(AuthContext);
	const location = useLocation();
	const from = location.state?.from;
	const redirectTo = from?.pathname === "/send" ? `${from.pathname}${from.search}` : "/tickets";

	const handleChangeInput = (e) => {
		setUser({ ...user, [e.target.name]: e.target.value });
	};

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const handleMouseDownPassword = (e) => {
		e.preventDefault();
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		handleLogin(user, redirectTo);
	};

	const register = () => {
		window.location.href = "/signup";
	};

	return (
        <div className={classes.root}>
            <CssBaseline />
            <video
                className={classes.video}
                autoPlay
                loop
                muted
                playsInline
                poster={wallpaper}
            >
                <source src={wallpaperVideo} type="video/mp4" />
            </video>
            <div className={classes.overlay} />
            <Paper className={classes.card}>
				<img src={logo} alt="Logo" className={classes.logo} />
				<form className={classes.form} noValidate onSubmit={handleSubmit}>
					<TextField
						variant="outlined"
						margin="normal"
						required
						fullWidth
						id="email"
						label={i18n.t("login.form.email")}
						name="email"
						value={user.email}
						onChange={handleChangeInput}
						autoComplete="email"
						autoFocus
					/>
					<TextField
						variant="outlined"
						margin="normal"
						required
						fullWidth
						name="password"
						label={i18n.t("login.form.password")}
						type={showPassword ? "text" : "password"}
						id="password"
						value={user.password}
						onChange={handleChangeInput}
						autoComplete="current-password"
						slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            onMouseDown={handleMouseDownPassword}
                                            size="small">
                                            {showPassword ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }
                        }}
					/>
					<Button
						type="submit"
						fullWidth
						variant="contained"
						color="primary"
						className={classes.submitButton}
					>
						{i18n.t("login.buttons.submit")}
					</Button>
					<Button
						fullWidth
						variant="outlined"
						color="secondary"
						className={classes.registerButton}
						onClick={register}
					>
						{i18n.t("login.buttons.register")}
					</Button>
					<Grid container sx={{
                        justifyContent: "center"
                    }}>
						<Grid>
							<Link
								variant="body2"
								component={RouterLink}
								to="/forget"
								className={classes.link}
								underline="hover"
								sx={{ color: "text.secondary", "&:hover": { color: "primary.main" } }}
							>
								{i18n.t("login.buttons.restart")}
							</Link>
						</Grid>
					</Grid>
				</form>
				<Box className={classes.copyright}>
					<Copyright />
				</Box>
			</Paper>
        </div>
    );
};

export default Login;
